import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Star, Upload, X } from "lucide-react";

import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { adminProductsService } from "../../../services/adminProducts";
import { api } from "../../../services/api";
import { formatApiError } from "../../../services/api";

const MAX_IMAGES = 5;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME = ["image/jpeg", "image/png", "image/webp"];

export function ProductFormModal({
  open,
  onOpenChange,
  editingProduct,
  onSaved,
}) {
  const { t } = useTranslation();
  const isEdit = Boolean(editingProduct);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  // For CREATE: local file objects staged until first save.
  // For EDIT: server-side image_urls, mutated via /images endpoints.
  const [localFiles, setLocalFiles] = useState([]); // File[]
  const [remoteUrls, setRemoteUrls] = useState([]); // string[]
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const emptyForm = useMemo(
    () => ({
      name: "",
      category: "",
      brand: "",
      sku: "",
      price_eur: "",
      in_stock: "",
      weight_kg: "",
      dimensions: "",
      description: "",
    }),
    []
  );
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setLocalFiles([]);
    if (editingProduct) {
      setForm({
        name: editingProduct.name || "",
        category: editingProduct.category || "",
        brand: editingProduct.brand || "",
        sku: editingProduct.sku || "",
        price_eur: editingProduct.price_eur ?? "",
        in_stock: String(editingProduct.in_stock ?? ""),
        weight_kg:
          editingProduct.weight_kg == null ? "" : String(editingProduct.weight_kg),
        dimensions: editingProduct.dimensions || "",
        description: editingProduct.description || "",
      });
      setRemoteUrls(
        Array.isArray(editingProduct.image_urls)
          ? editingProduct.image_urls
          : editingProduct.image_url
          ? [editingProduct.image_url]
          : []
      );
    } else {
      setForm(emptyForm);
      setRemoteUrls([]);
    }
  }, [open, editingProduct, emptyForm]);

  useEffect(() => {
    if (!open) return;
    const byLocale = (a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
    api.get("/categories")
      .then((r) => setCategories((r.data.items || []).slice().sort(byLocale)))
      .catch(() => {});
    api.get("/brands")
      .then((r) => setBrands((r.data.items || []).slice().sort(byLocale)))
      .catch(() => {});
  }, [open]);

  const totalImages = (isEdit ? remoteUrls.length : localFiles.length);
  const canAddImage = totalImages < MAX_IMAGES;

  const handleImagePick = async (file) => {
    if (!file) return;
    if (!ALLOWED_MIME.includes(file.type)) {
      toast.error(t("admin.products.err_image_type"));
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(t("admin.products.err_image_size"));
      return;
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (isEdit && editingProduct) {
      // Upload immediately to backend.
      setUploadingImage(true);
      try {
        const updated = await adminProductsService.appendImage(
          editingProduct.id,
          file
        );
        setRemoteUrls(updated.image_urls || []);
      } catch (err) {
        const d = err?.response?.data?.detail;
        if (d && typeof d === "object" && d.code === "IMAGE_LIMIT") {
          toast.error(t("admin.products.err_image_limit", { max: MAX_IMAGES }));
        } else {
          toast.error(formatApiError(err, t("common.error_generic")));
        }
      } finally {
        setUploadingImage(false);
      }
    } else {
      if (localFiles.length >= MAX_IMAGES) {
        toast.error(t("admin.products.err_image_limit", { max: MAX_IMAGES }));
        return;
      }
      setLocalFiles((prev) => [...prev, file]);
    }
  };

  const removeImageAt = async (index) => {
    if (isEdit && editingProduct) {
      try {
        const updated = await adminProductsService.deleteImage(
          editingProduct.id,
          index
        );
        setRemoteUrls(updated.image_urls || []);
      } catch (err) {
        toast.error(formatApiError(err, t("common.error_generic")));
      }
    } else {
      setLocalFiles((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2)
      e.name = t("admin.products.err_name");
    // Phase A11: only name is required. Everything else is optional.
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category || null,
        brand: form.brand || null,
        in_stock: form.in_stock === "" ? 0 : Number(form.in_stock),
        weight_kg: form.weight_kg === "" ? null : Number(form.weight_kg),
        dimensions: form.dimensions || null,
        description: form.description || null,
        sku: form.sku || null,
        price_eur: form.price_eur === "" ? null : Number(form.price_eur),
      };
      let productId;
      if (isEdit) {
        await adminProductsService.update(editingProduct.id, payload);
        productId = editingProduct.id;
        toast.success(t("admin.products.saved"));
      } else {
        const created = await adminProductsService.create(payload);
        productId = created.id;
        // Upload staged images serially so the backend can enforce the cap.
        for (const file of localFiles) {
          try {
            await adminProductsService.appendImage(productId, file);
          } catch (imgErr) {
            const d = imgErr?.response?.data?.detail;
            if (d && typeof d === "object" && d.code === "IMAGE_LIMIT") {
              toast.error(t("admin.products.err_image_limit", { max: MAX_IMAGES }));
              break;
            }
            toast.error(formatApiError(imgErr, t("common.error_generic")));
            break;
          }
        }
        toast.success(t("admin.products.created"));
      }
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "DUPLICATE") {
        toast.error(t("admin.products.err_duplicate"));
      } else {
        toast.error(formatApiError(err, t("common.error_generic")));
      }
    } finally {
      setSaving(false);
    }
  };

  const previews = isEdit
    ? remoteUrls.map((url, i) => ({ url, key: `r-${i}-${url}`, idx: i }))
    : localFiles.map((f, i) => ({
        url: URL.createObjectURL(f),
        key: `l-${i}-${f.name}-${f.size}`,
        idx: i,
      }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto bg-surface"
        data-testid="product-form-modal"
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t("admin.products.edit_title")
              : t("admin.products.create_title")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("admin.products.form_description")}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {/* Multi-image gallery */}
          <div className="md:col-span-2 space-y-2">
            <div className="flex items-baseline justify-between">
              <Label>{t("admin.products.field_images")}</Label>
              <span className="text-xs text-text-secondary" data-testid="product-form-image-count">
                {totalImages}/{MAX_IMAGES}
              </span>
            </div>
            <div className="flex flex-wrap gap-3" data-testid="product-form-image-gallery">
              {previews.map((p, i) => (
                <div
                  key={p.key}
                  className="group relative h-24 w-24 flex-none overflow-hidden rounded-md border border-border-subtle bg-offwhite"
                  data-testid={`product-form-image-slot-${i}`}
                >
                  <img
                    src={p.url}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                  {i === 0 && (
                    <span
                      className="pointer-events-none absolute left-1 top-1 inline-flex items-center gap-1 rounded-sm bg-charcoal/85 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-champagne"
                      data-testid="product-form-image-primary-badge"
                    >
                      <Star className="h-3 w-3" fill="currentColor" />
                      {t("admin.products.primary_badge")}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeImageAt(i)}
                    aria-label={t("common.delete")}
                    data-testid={`product-form-image-remove-${i}`}
                    className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-surface/90 text-status-unavailable opacity-0 shadow-card transition-opacity group-hover:opacity-100 focus:opacity-100"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {canAddImage && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  data-testid="product-form-image-pick"
                  className="flex h-24 w-24 flex-none flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border-subtle bg-offwhite text-text-secondary transition hover:border-charcoal hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne disabled:opacity-60"
                >
                  {uploadingImage ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Upload className="h-5 w-5" />
                      <span className="text-[10px] font-semibold uppercase tracking-wide">
                        {t("admin.products.add_image")}
                      </span>
                    </>
                  )}
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => handleImagePick(e.target.files?.[0])}
              data-testid="product-form-image-input"
              className="hidden"
            />
            <p className="text-xs text-text-secondary">
              {t("admin.products.image_help_multi", { max: MAX_IMAGES })}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>{t("admin.products.field_name")} *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="product-form-name"
              className="bg-surface border-border-subtle"
            />
            {errors.name && <p className="text-xs text-status-unavailable">{errors.name}</p>}
          </div>
          <div className="space-y-1.5">
            <Label>{t("admin.products.field_sku")}</Label>
            <Input
              value={form.sku}
              onChange={(e) =>
                setForm({ ...form, sku: e.target.value.toUpperCase() })
              }
              maxLength={50}
              data-testid="product-form-sku"
              className="bg-surface border-border-subtle font-mono uppercase"
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t("admin.products.field_category")}</Label>
            <Select
              value={form.category || "__none__"}
              onValueChange={(v) =>
                setForm({ ...form, category: v === "__none__" ? "" : v })
              }
            >
              <SelectTrigger
                data-testid="product-form-category"
                className="bg-surface border-border-subtle"
              >
                <SelectValue placeholder={t("admin.products.select_category")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">
                  {t("admin.products.category_none")}
                </SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t("admin.products.field_brand")}</Label>
            <Select
              value={form.brand || "__none__"}
              onValueChange={(v) =>
                setForm({ ...form, brand: v === "__none__" ? "" : v })
              }
            >
              <SelectTrigger
                data-testid="product-form-brand"
                className="bg-surface border-border-subtle"
              >
                <SelectValue placeholder={t("admin.products.select_brand")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">
                  {t("admin.products.brand_none")}
                </SelectItem>
                {brands.map((b) => (
                  <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>{t("admin.products.field_stock")}</Label>
            <Input
              type="number"
              min="0"
              value={form.in_stock}
              onChange={(e) => setForm({ ...form, in_stock: e.target.value })}
              data-testid="product-form-stock"
              className="bg-surface border-border-subtle"
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("admin.products.field_price")}</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={form.price_eur}
              onChange={(e) => setForm({ ...form, price_eur: e.target.value })}
              data-testid="product-form-price"
              className="bg-surface border-border-subtle"
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t("admin.products.field_dimensions")}</Label>
            <Input
              value={form.dimensions}
              onChange={(e) => setForm({ ...form, dimensions: e.target.value })}
              placeholder={t("admin.products.dimensions_placeholder")}
              data-testid="product-form-dimensions"
              className="bg-surface border-border-subtle"
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("admin.products.field_weight")}</Label>
            <Input
              type="number"
              min="0"
              step="0.1"
              value={form.weight_kg}
              onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
              data-testid="product-form-weight"
              className="bg-surface border-border-subtle"
            />
          </div>

          <div className="md:col-span-2 space-y-1.5">
            <Label>{t("admin.products.field_description")}</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={4}
              maxLength={1000}
              data-testid="product-form-description"
              className="bg-surface border-border-subtle"
            />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2 border-t border-border-subtle pt-4">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="product-form-cancel"
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={saving}
            className="bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-champagne"
            data-testid="product-form-submit"
          >
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {isEdit ? t("common.save") : t("admin.products.create_submit")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
